import { trackEvent, uuidV4, isWincApp } from '@readcloud/common';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@readcloud/component-library';
import { Job } from '@readcloud/data';
import copy from 'copy-to-clipboard';
import { ClientSmallBrand } from '../../../types/TopToolbarConstants';
import { getDirectNativeAppUrl, getReaderExternalUrl } from '../../../constants';

// Check if we're running in Tauri environment
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

// Determine if we're in test environment using VITE_APP_ENV
const isTestEnvironment = () => {
  // Use the build-time environment variable to determine test vs production
  const appEnv = import.meta.env.VITE_APP_ENV;
  return appEnv === 'native-test' || appEnv === 'development';
};

/**
 * Get the appropriate brand for deep links.
 * Only winc gets its own scheme, all other brands use 'rc' (readcloud).
 */
const getDeepLinkBrand = (): 'rc' | 'winc' => {
  return isWincApp() ? 'winc' : 'rc';
};

/**
 * Get the web app base URL for sharing links.
 * In Tauri, we hardcode to the appropriate production or test URL.
 * In webapp, we use the current origin.
 */
const getWebAppBaseUrl = () => {
  const isTest = isTestEnvironment();
  const isWinc = isWincApp();

  if (isTauri) {
    // In Tauri, hardcode to appropriate domain
    if (isWinc) {
      return isTest
        ? 'https://winc.app.readcloud-test.com'
        : 'https://winc.app.readcloud.com';
    }
    return isTest
      ? 'https://app.readcloud-test.com'
      : 'https://app.readcloud.com';
  }

  // In webapp, use current origin but normalize api.readcloud.com to app.readcloud.com
  const origin = window.location.origin;
  if (origin.includes('api.readcloud.com')) {
    return 'https://app.readcloud.com';
  }
  return origin;
};

interface IExternalUrlModal {
  brand: ClientSmallBrand;
  bookId: string;
  cloudId: string;
  location: string;
  isOpen: boolean;
  toggle: () => void;
  onCopy?: (job: Job) => void;
}

export const ExternalUrlModal: React.FC<IExternalUrlModal> = (props) => {
  return (
    <Modal isOpen={props.isOpen} toggle={props.toggle}>
      <ModalHeader>
        <ModalTitle>Copy Link</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <p>Please use the below buttons for the correct URL.</p>
      </ModalBody>
      <ModalFooter>
        <Button
          className="max-sm:rc-text-xs"
          onClick={() => {
            trackEvent('Webapp/CopiedNativeURL', {
              bookId: props.bookId,
              cloudId: props.cloudId,
              location: props.location,
            });
            // Tauri uses direct deep links; web uses the old linker redirect
            const nativeUrl = isTauri
              ? getDirectNativeAppUrl(
                  props.bookId,
                  props.cloudId,
                  props.location,
                  getDeepLinkBrand()
                )
              : getReaderExternalUrl(
                  props.brand || getDeepLinkBrand(),
                  props.bookId,
                  props.cloudId,
                  props.location
                );
            copy(nativeUrl);
            if (props.onCopy) {
              props.onCopy({
                id: uuidV4(),
                description: 'Native App URL copied to clipboard!',
              });
            }
            props.toggle();
          }}
        >
          Copy Native App URL
        </Button>
        <Button
          className="max-sm:rc-text-xs"
          onClick={() => {
            trackEvent('Webapp/CopiedWebAppURL', {
              bookId: props.bookId,
              cloudId: props.cloudId,
              location: props.location,
            });
            // Build web app URL using the appropriate base
            const baseUrl = getWebAppBaseUrl();
            const webUrl = `${baseUrl}/read/${props.cloudId}/${props.bookId}?p=${encodeURIComponent(props.location)}`;
            copy(webUrl);
            if (props.onCopy) {
              props.onCopy({
                id: uuidV4(),
                description: 'Web App URL copied to clipboard!',
              });
            }
            props.toggle();
          }}
        >
          Copy Web App URL
        </Button>
      </ModalFooter>
    </Modal>
  );
};
